import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Stepper,
  Step,
  StepLabel,
  StepButton,
  Typography,
  Box,
  Paper,
  styled,
  Skeleton,
  Chip
} from "@mui/material";
import { Tooltip } from '@mui/material';
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import SportsGymnasticsIcon from '@mui/icons-material/SportsGymnastics';
import GavelIcon from "@mui/icons-material/Gavel";
import PersonIcon from "@mui/icons-material/Person";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SummarizeIcon from "@mui/icons-material/Summarize";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import { StepIconProps } from '@mui/material/StepIcon';

import { useRegistrationStatus } from "../hooks/useRegistrationStatus";
import { handleApiError } from "../utils/apiErrorHandler";

// Define registration steps
const steps = [
  { 
    step: "athletes", 
    label: "Sportler melden", 
    icon: <SportsGymnasticsIcon />,
    description: "Sportler für den Wettkampf anmelden" 
  },
  { 
    step: "judges", 
    label: "Kampfrichter melden", 
    icon: <GavelIcon />,
    description: "Kampfrichter für den Wettkampf anmelden" 
  },
  { 
    step: "officials", 
    label: "Offizielle melden", 
    icon: <PersonIcon />,
    description: "Offizielle für den Wettkampf anmelden" 
  },
  { 
    step: "additional", 
    label: "Zusatzbuchungen", 
    icon: <ShoppingCartIcon />,
    description: "Verpflegung und Unterkünfte buchen" 
  },
  { 
    step: "summary", 
    label: "Übersicht", 
    icon: <SummarizeIcon />,
    description: "Meldung überprüfen und abschließen" 
  }
];

// Styling for the connector between steps
const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient(95deg, #1976d2 0%, #42a5f5 100%)',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient(95deg, #1976d2 0%, #42a5f5 100%)',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
    borderRadius: 1,
  },
}));

// Custom styled step icon
const ColorlibStepIconRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean; error?: boolean; warning?: boolean; };
}>(({ theme, ownerState }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#ccc',
  zIndex: 1,
  color: '#fff',
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  ...(ownerState.active && {
    backgroundImage: 'linear-gradient(136deg, #1976d2 0%, #42a5f5 100%)',
    boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
  }),
  ...(ownerState.completed && {
    backgroundImage: 'linear-gradient(136deg, #1976d2 0%, #42a5f5 100%)',
  }),
  ...(ownerState.error && {
    backgroundImage: 'linear-gradient(136deg, #d32f2f 0%, #f44336 100%)',
    boxShadow: '0 4px 10px 0 rgba(211,47,47,.25)',
  }),
  ...(ownerState.warning && {
    backgroundImage: 'linear-gradient(136deg, #ff9800 0%, #ffc107 100%)',
    boxShadow: '0 4px 10px 0 rgba(255,152,0,.25)',
  }),
}));

// Error/warning indicator that appears on top of the step icon
const StatusIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: -5,
  right: -5,
  width: 20,
  height: 20,
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'white',
  boxShadow: '0 2px 4px 0 rgba(0,0,0,0.2)',
}));

// Custom step icon component
function ColorlibStepIcon(props: StepIconProps & { 
  error?: ValidationItem | undefined; 
  warning?: ValidationItem | undefined; 
  stepIcon: React.ReactNode 
}) {
  const { active, completed, className, error, warning, stepIcon } = props;
  
  // Check status property instead of the object itself
  const hasError = error?.status === true;
  const hasWarning = warning?.status === true;
  const tooltipText = hasError ? error.message : (hasWarning ? warning.message : '');

  return (
    <Tooltip title={tooltipText} arrow placement="top">
      <div>
        <ColorlibStepIconRoot ownerState={{ completed, active, error: hasError, warning: hasWarning }} className={className}>
          {stepIcon}
          {hasError && (
            <StatusIndicator>
              <ErrorIcon color="error" sx={{ fontSize: 16 }} />
            </StatusIndicator>
          )}
          {hasWarning && !hasError && (
            <StatusIndicator>
              <WarningIcon color="warning" sx={{ fontSize: 16 }} />
            </StatusIndicator>
          )}
        </ColorlibStepIconRoot>
      </div>
    </Tooltip>
  );
}

function StepIconWrapper(props: StepIconProps & { 
  errorItem?: ValidationItem; 
  warningItem?: ValidationItem; 
  stepIcon: React.ReactNode 
}) {
  const { active, completed, className, errorItem, warningItem, stepIcon, ...otherProps } = props;
  
  const hasError = errorItem?.status === true;
  const hasWarning = warningItem?.status === true;
  const tooltipText = hasError ? errorItem.message : (hasWarning ? warningItem.message : '');

  return (
    <Tooltip title={tooltipText} arrow placement="top">
      <div>
        <ColorlibStepIconRoot ownerState={{ completed, active, error: hasError, warning: hasWarning }} className={className}>
          {stepIcon}
          {hasError && (
            <StatusIndicator>
              <ErrorIcon color="error" sx={{ fontSize: 16 }} />
            </StatusIndicator>
          )}
          {hasWarning && !hasError && (
            <StatusIndicator>
              <WarningIcon color="warning" sx={{ fontSize: 16 }} />
            </StatusIndicator>
          )}
        </ColorlibStepIconRoot>
      </div>
    </Tooltip>
  );
}

interface ValidationItem {
  status: boolean;
  message: string;
}

interface RegistrationProgressProps {
  errors?: Record<string, ValidationItem>;
  warnings?: Record<string, ValidationItem>;
  enableOfficial?: boolean;
}

const RegistrationProgress: React.FC<RegistrationProgressProps> = ({ errors = {}, warnings = {}, enableOfficial = true }) => {
  const { competitionId } = useParams(); 
  const location = useLocation();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(-1); // Start mit -1 statt 0

  // Use the new registration status hook
  const registrationStatus = useRegistrationStatus(competitionId);

  // Determine active step based on current URL and registration status
  useEffect(() => {
    // Warte bis der Status geladen ist
    if (registrationStatus.loading) return;
    
    const currentPath = location.pathname;
    
    if (registrationStatus.isClosed) {
      // Bei geschlossener Anmeldung ist Summary immer aktiv
      setActiveStep(0);
    } else {
      // Bei offener Anmeldung basierend auf URL (nur unter den für diesen Wettkampf aktivierten Steps)
      const enabledSteps = steps.filter(step => step.step !== "officials" || enableOfficial);
      const stepIndex = enabledSteps.findIndex(step =>
        currentPath.includes(`/${step.step}`)
      );

      if (stepIndex !== -1) {
        setActiveStep(stepIndex);
      }
    }
  }, [location.pathname, registrationStatus.loading, registrationStatus.isClosed, enableOfficial]);

  // Wenn geschlossen und wir uns nicht auf der Summary-Seite befinden, navigieren
  useEffect(() => {
    // Nur navigieren, wenn alles geladen ist
    if (registrationStatus.isClosed && !registrationStatus.loading && !location.pathname.includes('/summary')) {
      navigate(`/competition-registration/${competitionId}/summary`);
    }
  }, [registrationStatus.isClosed, registrationStatus.loading, location.pathname, navigate, competitionId]);

  // Wenn "Offizielle melden" deaktiviert ist, aber der Pfad direkt aufgerufen wurde, umleiten
  useEffect(() => {
    if (!enableOfficial && !registrationStatus.loading && location.pathname.includes('/officials')) {
      navigate(`/competition-registration/${competitionId}/athletes`);
    }
  }, [enableOfficial, registrationStatus.loading, location.pathname, navigate, competitionId]);

  // Function to handle step clicks
  const handleStepClick = (step: string) => {
    if (registrationStatus.isClosed && step !== "summary") return; // Only allow summary when closed
    navigate(`/competition-registration/${competitionId}/${step}`);
  };

  // Show loading skeleton while checking registration status OR while activeStep is not set
  if (registrationStatus.loading || activeStep === -1) {
    return (
      <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
          {[1, 2, 3, 4, 5].map((item) => (
            <Box key={item} sx={{ textAlign: 'center' }}>
              <Skeleton variant="circular" width={50} height={50} sx={{ mx: 'auto', mb: 1 }} />
              <Skeleton variant="text" width={80} />
              <Skeleton variant="text" width={100} height={14} />
            </Box>
          ))}
        </Box>
      </Paper>
    );
  }

  // Filter steps when competition is closed (show only summary) or when a step is disabled for this competition
  const enabledSteps = steps.filter(step => step.step !== "officials" || enableOfficial);
  const visibleSteps = registrationStatus.isClosed
    ? enabledSteps.filter(step => step.step === "summary")
    : enabledSteps;

  // Format deadline message
  const getStatusMessage = () => {
    const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('de-DE');
    
    switch (registrationStatus.status) {
      case 'not_yet_open':
        return `Meldung noch nicht geöffnet. Meldeschluss: ${formatDate(registrationStatus.originalDeadline)}`;
      
      case 'open':
        return `Meldung offen bis: ${formatDate(registrationStatus.deadline)}`;
      
      case 'extended':
        return `Meldung verlängert bis: ${formatDate(registrationStatus.deadline)}`;
      
      case 'closed':
        return 'Die Anmeldefrist ist abgelaufen. Du kannst nur noch die Übersicht einsehen.';
      
      default:
        return '';
    }
  };

  // Get status color
  const getStatusColor = () => {
    switch (registrationStatus.status) {
      case 'not_yet_open':
        return 'info.main';
      case 'open':
        return 'success.main';
      case 'extended':
        return 'warning.main';
      case 'closed':
        return 'error.main';
      default:
        return 'text.secondary';
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
      {/* Status message */}
      <Box sx={{ mb: 2, textAlign: 'center' }}>
        <Typography 
          variant="body2" 
          color={getStatusColor()}
          sx={{ mb: 1 }}
        >
          {getStatusMessage()}
        </Typography>
        
        {/* Extension chip for extended registrations */}
        {registrationStatus.isExtended && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 1 }}>
            <Chip 
              icon={<AccessTimeIcon />}
              label="Meldung verlängert" 
              color="warning" 
              size="small" 
              variant="outlined"
            />
            {registrationStatus.extension?.reason && (
              <Chip 
                label={registrationStatus.extension.reason} 
                size="small" 
                variant="outlined"
              />
            )}
          </Box>
        )}

        {/* Original deadline info for extended registrations */}
        {registrationStatus.isExtended && (
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ display: 'block' }}
          >
            Ursprünglicher Meldeschluss: {new Date(registrationStatus.originalDeadline).toLocaleDateString('de-DE')}
          </Typography>
        )}
      </Box>
      
      <Stepper 
        alternativeLabel 
        activeStep={registrationStatus.isClosed ? 0 : activeStep}
        connector={<ColorlibConnector />}
      >
        {visibleSteps.map((step, index) => {
          const stepError = errors[step.step];
          const stepWarning = warnings[step.step];
          const hasError = stepError?.status === true;
          const hasWarning = stepWarning?.status === true;
          
          // Für geschlossene Anmeldungen: Summary ist immer aktiv und completed
          const isActive = registrationStatus.isClosed ? true : (index === activeStep);
          const isCompleted = registrationStatus.isClosed ? true : (index < activeStep);

          return (
            <Step key={step.step} completed={isCompleted}>
              <StepButton 
                onClick={() => handleStepClick(step.step)}
                disabled={registrationStatus.isClosed && step.step !== "summary"}
              >
                <StepLabel 
                  StepIconComponent={(props) => 
                    <StepIconWrapper 
                      {...props}
                      active={isActive}
                      completed={isCompleted}
                      errorItem={stepError}
                      warningItem={stepWarning}
                      stepIcon={step.icon}
                    />
                  }
                >
                  <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
                    {step.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
                    {step.description}
                  </Typography>
                  
                  {/* Optional message display */}
                  {(hasError || hasWarning) && !registrationStatus.isClosed && (
                    <Typography 
                      variant="caption" 
                      color={hasError ? "error" : "warning.main"}
                      sx={{ display: 'block', mt: 0.5 }}
                    >
                      {hasError ? stepError.message : stepWarning.message}
                    </Typography>
                  )}
                </StepLabel>
              </StepButton>
            </Step>
          );
        })}
      </Stepper>
    </Paper>
  );
};

export default RegistrationProgress;